import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Columns, Check } from 'lucide-react';
import { Button } from '../Button/Button';

export interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  required?: boolean;
}

export interface ColumnChooserProps {
  columns: ColumnConfig[];
  onChange: (columns: ColumnConfig[]) => void;
  onReset?: () => void;
  className?: string;
}

export function ColumnChooser({
  columns,
  onChange,
  onReset,
  className
}: ColumnChooserProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = (key: string) => {
    const updated = columns.map(col =>
      col.key === key && !col.required
        ? { ...col, visible: !col.visible }
        : col
    );
    onChange(updated);
  };

  const handleSelectAll = () => {
    const updated = columns.map(col => ({ ...col, visible: true }));
    onChange(updated);
  };

  const handleDeselectAll = () => {
    const updated = columns.map(col =>
      col.required ? col : { ...col, visible: false }
    );
    onChange(updated);
  };

  const visibleCount = columns.filter(c => c.visible).length;

  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        leftIcon={<Columns className="w-4 h-4" />}
        aria-label="Choose visible columns"
        aria-expanded={isOpen}
      >
        Columns ({visibleCount})
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-3 border-b border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-900">
                Visible Columns
              </h3>
              <span className="text-xs text-gray-500">
                {visibleCount} of {columns.length}
              </span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSelectAll}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded px-1"
              >
                Select All
              </button>
              <span className="text-xs text-gray-300">|</span>
              <button
                type="button"
                onClick={handleDeselectAll}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded px-1"
              >
                Deselect All
              </button>
              {onReset && (
                <>
                  <span className="text-xs text-gray-300">|</span>
                  <button
                    type="button"
                    onClick={() => {
                      onReset();
                      setIsOpen(false);
                    }}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded px-1"
                  >
                    Reset
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto p-2">
            {columns.map((column) => (
              <button
                key={column.key}
                type="button"
                onClick={() => handleToggle(column.key)}
                disabled={column.required}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors',
                  'hover:bg-gray-50 focus:outline-none focus:bg-gray-50',
                  column.required && 'opacity-60 cursor-not-allowed'
                )}
                aria-label={`${column.visible ? 'Hide' : 'Show'} ${column.label} column`}
              >
                <div
                  className={cn(
                    'flex-shrink-0 w-4 h-4 border-2 rounded flex items-center justify-center transition-colors',
                    column.visible
                      ? 'bg-primary-600 border-primary-600'
                      : 'border-gray-300 bg-white',
                    column.required && 'bg-gray-300 border-gray-300'
                  )}
                >
                  {column.visible && (
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  )}
                </div>
                <span
                  className={cn(
                    'flex-1 text-left',
                    column.visible ? 'text-gray-900 font-medium' : 'text-gray-600'
                  )}
                >
                  {column.label}
                  {column.required && (
                    <span className="ml-1 text-xs text-gray-500">(required)</span>
                  )}
                </span>
              </button>
            ))}
          </div>

          <div className="p-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <p className="text-xs text-gray-500 px-2">
              Required columns are always visible
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
